<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import { getOrders } from '@/api/orders'
import { getCustomerList } from '@/api/customers'
import { formatedDate } from '@/constants'

const customerList = ref<any>([])
const router = useRouter()
const orderList = ref<any>([])
const loading = ref<boolean>(true)
const searchQuery = ref('')

const getCustomer = (id: string) => {
  return customerList.value.find((customer: { id: string }) => customer.id === id)?.businessName
}

const filteredOrderList = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return orderList.value.filter(
    (order: { customerId: string; orderNumber: number; isCancelled: boolean }) =>
    order.isCancelled && (
      getCustomer(order.customerId)?.toLowerCase().includes(query) || order.orderNumber.toString().includes(query)
    )
  )
})

onMounted(async () => {
  orderList.value = await getOrders()
  customerList.value = await getCustomerList()
  loading.value = false
})
</script>

<template>
  <PageHeader
    title="Cancelled Orders"
    :description="`Total: ${orderList.length}`"
    buttonText="Back to Orders"
    buttonIcon="material-symbols:arrow-back"
    :buttonClick="() => router.push('/orders')"
  />
  <DataTable
    :value="filteredOrderList"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} cancelled orders"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
    class="w-full"
  >
    <template #empty> <p class="text-center">No cancelled orders found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />
      </div>
    </template>

    <Column field="orderNumber" header="Order Number">
      <template #body="slotProps">
        <RouterLink
          :to="`/orders/${slotProps.data.id}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.orderNumber }}
        </RouterLink>
      </template>
    </Column>
    <Column field="customerId" header="Customer Name">
      <template #body="slotProps">
        <RouterLink
            :to="`/customers/${slotProps.data.customerId}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getCustomer(slotProps.data.customerId) }}
        </RouterLink>
      </template>
    </Column>

    <Column field="dateReceived" header="Date Received" >
      <template #body="slotProps">
        {{ formatedDate(slotProps.data.dateReceived) }}
      </template>
    </Column>

    <Column field="customerPurchaseOrderNumber" header="Customer Purchase Order Number"></Column>

    <Column field="action">
      <template #body="slotProps">
        <RouterLink
          :to="`/orders/${slotProps.data.id}`"
          class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span> More Details</span>
          <Icon
            icon="material-symbols:more-up"
            class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>
    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>

